/**
 * Public Classes API Hook
 *
 * Fetches public class listings without authentication.
 * Uses native fetch since no auth token is required.
 */

import { useQuery } from "@tanstack/react-query"

import type { PublicClassFilters, PublicClassListResponse } from "../types"

// Ensure API base URL always includes /api/v1 path
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL
  if (!envUrl) {
    return "/api/v1"
  }
  // If VITE_API_URL is set but doesn't include /api/v1, append it
  if (envUrl.endsWith("/api/v1")) {
    return envUrl
  }
  // Remove trailing slash if present and append /api/v1
  return `${envUrl.replace(/\/$/, "")}/api/v1`
}

const API_BASE_URL = getApiBaseUrl()

/**
 * Build query string from filters object
 */
function buildQueryString(filters: PublicClassFilters): string {
  const params = new URLSearchParams()

  if (filters.session_id) params.append("session_id", filters.session_id)
  if (filters.level) params.append("level", filters.level)
  if (filters.day_of_week) params.append("day_of_week", filters.day_of_week)
  if (filters.time_of_day) params.append("time_of_day", filters.time_of_day)
  if (filters.location) params.append("location", filters.location)
  if (filters.sort_by) params.append("sort_by", filters.sort_by)
  if (filters.limit !== undefined)
    params.append("limit", filters.limit.toString())
  if (filters.offset !== undefined)
    params.append("offset", filters.offset.toString())

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ""
}

/**
 * Fetch public class listings
 *
 * @param filters - Optional filters for the class list
 * @returns TanStack Query result with public class data
 */
export function usePublicClasses(filters: PublicClassFilters = {}) {
  return useQuery({
    queryKey: ["public-classes", filters],
    queryFn: async (): Promise<PublicClassListResponse> => {
      const queryString = buildQueryString(filters)
      const url = `${API_BASE_URL}/public/classes${queryString}`

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch public classes: ${response.statusText}`,
        )
      }

      const data = await response.json()

      // Handle standard API response wrapper if present
      if (data.data) {
        return data.data as PublicClassListResponse
      }

      return data as PublicClassListResponse
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - public data can be cached longer
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
